import React, { useState, useContext, useEffect } from "react";
import InputText from "../../components/ui/input_text/InputText";
import Button from "../../components/ui/button/Button";
import DataContext from "../../context/DataContext";
import InputColor from "../../components/ui/input_color/InputColor";
import InputList from "../../components/ui/input_list/InputList";
import styles from "./styles/Customization.module.css";
import { axiosSW, axiosSW2 } from "../../api/axiosDefaults";
import Modal from "./Modal";
import LogIn from "./LogIn";
import { useTranslation } from "react-i18next";
import InputFile from "../../components/ui/input_file/InputFile";
import { useLocation, useParams } from "react-router-dom";
import Spinner from "../../components/ui/spinner/Spinner";
import enDynamicDefault from "../../assets/enDynamicDefault.json";
import { getMaxId } from "../../utils/utils";

import CustomSearchGroup from "./CustomSearchGroup";
import ButtonExpand from "../../components/ui/button_expand/ButtonExpand";
import useCurrebtBU from "../../hooks/useCurrentBU";
import POSDataContext from "../../context/POSDataContext";
import RTEditor from "./RTEditor";
import InputCheck from "../../components/ui/input_check/InputCheck";
import SliderToggle from "../../components/ui/slider_toggle/SliderToggle";

const LINK_TYPES = [
    "email",
    "phone",
    "location",
    "link",
    "static",
    "int-link",
    "social-media",
    "blank",
];

export const EMPTY_DOC = {
    type: "doc",
    content: [
        {
            type: "paragraph",
        },
    ],
};
const LINK_ICONS = [
    "location",
    "phone",
    "fax",
    "envelope",
    "file",
    "linkedin",
    "youtube",
    "facebook",
    "twitter",
    "no logo",
];

const Customization = () => {
    const {
        currentCustom,
        localizations,
        serviceData,
        isServiceDataAvailable,
        connection,
        languages,
        currentBU,
        homePath,
        handleError,
        JWTToken,
    } = useContext(DataContext);
    const { handleErrorPos } = useContext(POSDataContext);

    const [isAdmin, setIsAdmin] = useState(false);
    const [isSuperUser, setIsSuperUser] = useState(false);
    const [customizations, setCustomizations] = useState({});
    const [currentLocalizations, setCurrentLocalizations] = useState({});
    const [cardsVisible, setCardsVisible] = useState({
        0: false,
        1: false,
        2: false,
        3: false,
    });
    const [termsEditorVisible, setTermsEditorVisible] = useState({});
    const [disclaimerEditorVisible, setDisclaimerEditorVisible] = useState({});
    // const [currentPOSCustomizations, setCurrentPOSCustomizations] =
    //     useState(POS_CUSTOM);
    const [inProcess, setInProcess] = useState(false);
    const [success, setSuccess] = useState(false);
    const [resultModal, setResultModal] = useState(false);
    const [showPOS, setShowPOS] = useState(false);
    const [fieldDefs, setFieldDefs] = useState([]);
    const [fieldNames, setFieldNames] = useState([]);
    const { t } = useTranslation();

    const { bUser } = useParams();
    const location = useLocation();

    const [termsJson, setTermsJson] = useState({});
    const [disclaimerJson, setDisclaimerJson] = useState({});
    const [termsFileImport, setTermsFileImport] = useState({});

    // set the current bUser in the context
    useCurrebtBU(bUser);

    // Fetch service data on page load if not avialable
    useEffect(() => {
        bUser &&
            JWTToken &&
            (!isServiceDataAvailable || bUser !== currentBU) &&
            serviceData(bUser);
    }, [bUser, isServiceDataAvailable, JWTToken]);

    // Set the showPOS state based on the current path
    // this is used to determine if the POS or Payment customization  should be shown
    useEffect(() => {
        setShowPOS(location?.pathname.includes("/pos") ? true : false);
    }, [bUser, homePath]);

    // Set the customisation state with data from currentCustom
    useEffect(() => {
        localizations &&
            setCurrentLocalizations(formatResourcesForFormInput(localizations));
        if (currentCustom) {
            setCustomizations(currentCustom);

            setFieldDefs(currentCustom?.fieldDefs);

            setFieldNames(extractFieldsList(currentCustom?.fieldDefs));
        }
        currentCustom && setCustomizations(currentCustom);
    }, [currentCustom, localizations]);

    /**
     * Extract the field name from an array of objects (fieldDefs) and return a new array
     * @param {Array} array
     * @returns {Array} array of fields names
     */
    const extractFieldsList = (array) => {
        if (array && array.length > 0) {
            return array.map((item) => item.fieldName);
        }
    };

    useEffect(() => {
        if (!languages) return;
        const currentTermsEditorVisible = Object.fromEntries(
            languages.map((key) => [key, false]),
        );
        setTermsEditorVisible(currentTermsEditorVisible);

        const currentDisclaimerEditorVisible = Object.fromEntries(
            languages.map((key) => [key, false]),
        );

        setDisclaimerEditorVisible(currentDisclaimerEditorVisible);
    }, [languages]);

    /**
     * Set the label field for each field in the POS customization object
     * by comparing the field name and adding the corresponding label to the object
     * @param {*} data
     * @returns
     */
    const addLabelField = (data) => {
        const newPos =
            (data && data?.pos && JSON.parse(JSON.stringify(data?.pos))) || {};
        Object.keys(newPos).forEach((key) => {
            newPos?.[key] &&
                newPos?.[key].length > 0 &&
                newPos?.[key].forEach((group) => {
                    group.fields.forEach((field) => {
                        const match = fieldDefs.find(
                            (category) =>
                                category.fieldName === field.fieldName,
                        );
                        if (match) {
                            field.label = match.label;
                            field.type = match.type ? match.type : "text";
                        }
                    });
                });
        });

        return { ...customizations, pos: newPos };
    };

    /**
     * Format the localizations object to be used in the form input
     * and set the dynamic content to the default en language file if there is no dynamic content
     * @param {Array} array of localizations objects
     * @returns  {Object} reformatted object with the language code as a key
     * and an object with the dynamic content as a key value pairs
     */
    const formatResourcesForFormInput = (array) => {
        const reformatted = {};

        let defaultDynamic = null;
        if (!array || array.length === 0) {
            return {};
        }

        array.map((obj) => {
            const languageCode = obj["culture"];
            if (languageCode === "en") defaultDynamic = obj.dynamicContent;
            const dynamic = obj.dynamicContent
                ? JSON.parse(obj.dynamicContent)
                : defaultDynamic
                  ? JSON.parse(defaultDynamic)
                  : enDynamicDefault;

            reformatted[languageCode] = { dynamic: dynamic };
        });

        const termsByCulture = Object.entries(reformatted).reduce(
            (acc, [culture, value]) => {
                const dynamic = value.dynamic || {};

                const termsContent =
                    dynamic.termsContent &&
                    Object.keys(dynamic.termsContent).length > 0
                        ? dynamic.termsContent
                        : EMPTY_DOC;

                acc[culture] = termsContent;
                return acc;
            },
            {},
        );

        setTermsJson(termsByCulture);

        const disclaimerByCulture = Object.entries(reformatted).reduce(
            (acc, [culture, value]) => {
                const dynamic = value.dynamic || {};

                const disclaimerContent =
                    dynamic.disclaimerContent &&
                    Object.keys(dynamic.disclaimerContent).length > 0
                        ? dynamic.disclaimerContent
                        : EMPTY_DOC;

                acc[culture] = disclaimerContent;
                return acc;
            },
            {},
        );

        setDisclaimerJson(disclaimerByCulture);

        return reformatted;
    };

    /**
     * Format the localizations object from the input form to be used in the API
     * @param {Object} obj object of localizations to be formatted for the API
     * @returns  {Array} array of objects with the culture and content as key value pairs
     */
    const formatResourcesForAPI = (obj) => {
        const convertedArray = Object.keys(obj).map(
            (culture) => {
                const dynamic = { ...obj[culture].dynamic };

                dynamic.termsContent = termsJson?.[culture] ?? EMPTY_DOC;
                dynamic.disclaimerContent =
                    disclaimerJson?.[culture] ?? EMPTY_DOC;

                if (showPOS) {
                    return {
                        Culture: culture,
                        content: JSON.stringify(dynamic),
                    };
                } else
                    return {
                        cultureCode: culture,
                        content: JSON.stringify(dynamic),
                    };
            },

            //     ({
            //     Culture: key, // language code
            //     Content: JSON.stringify(obj[key].dynamic),
            // })
        );

        return convertedArray;
    };

    // const formatResourcesForAPI = (obj) => {
    //     const convertedArray = Object.keys(obj).map((key) => ({
    //         Culture: key, // language code
    //         Content: JSON.stringify(obj[key].dynamic),
    //     }));

    //     return convertedArray;
    // };

    /**
     * Set the dynamic content of an input field to the equivalent value from the default en language if the field is left empty
     */
    const setLocalizationsEmptyFields = () => {
        const defaultLang = languages[0];

        Object.keys(currentLocalizations).forEach((lang) => {
            if (lang.split("-")[0] === "en") return; // Skip the "en" language

            const currentDynamic = currentLocalizations[lang].dynamic;
            const defaultDynamic = currentLocalizations[defaultLang]?.dynamic;

            if (!currentDynamic) {
                currentLocalizations[lang].dynamic = {};
            }

            defaultDynamic &&
                Object.keys(defaultDynamic).forEach((key) => {
                    if (!currentLocalizations[lang].dynamic[key]) {
                        currentLocalizations[lang].dynamic[key] =
                            defaultDynamic[key] || "";
                    }
                });
        });
    };

    /**
     * Submit handle
     * Submit customization object to the corresponding API endpoint depending on the showPOS state
     * @param {event} event the event object
     */
    const handleSubmit = async (event) => {
        event.preventDefault();

        setLocalizationsEmptyFields();

        const customizationsPayLoad = JSON.stringify(
            addLabelField(customizations),
        );

        const localizationsPayLoad =
            formatResourcesForAPI(currentLocalizations);

        setInProcess(true);
        setResultModal(true);

        try {
            const conficurationPayloadObject = {
                customerId: showPOS ? connection?.customerID : connection?.id,
                productCode: connection?.productInfo?.code,
                DynamicLocalizations: localizationsPayLoad,

                CustomizationContent: customizationsPayLoad,
            };

            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing

            // console.log(conficurationPayloadObject);

            // console.log(
            //     JSON.parse(conficurationPayloadObject?.CustomizationContent),
            // );
            // setSuccess(true);
            // setInProcess(false);
            // return;

            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing
            // --------------------------------------------------------------------------- testing

            if (showPOS) {
                const { data } = await axiosSW.post(
                    "/Configuration/pos/SaveLocalizationAndCustomization",
                    conficurationPayloadObject,
                );
            } else {
                const { data } = await axiosSW2.post(
                    "customers/configuration",
                    conficurationPayloadObject,
                );
            }

            setSuccess(true);
            // remove from local storage after successful submission
            // to insure that the new data is fetched correctly from the API
            sessionStorage.removeItem("currentCustom");
            setInProcess(false);
        } catch (error) {
            setSuccess(false);
            setInProcess(false);
            console.error("There was an error saving the data!", error);
        }
    };

    /**
     * Change handle slider
     * @param {event} event the event object
     */
    const handleChangeSlider = (event) => {
        setShowPOS(event.target.checked);
    };

    /**
     * Change handle
     * @param {event} event the event object
     */
    const handleChange = (event) => {
        setCustomizations((prevCustomizations) => ({
            ...prevCustomizations,
            [event.target.name]: event.target.value,
        }));
    };

    /**
     * Change handle for localizations
     * @param {event} event the event object
     */
    const handleChangeLocal = (event) => {
        const lang = event.target.dataset.ex;
        setCurrentLocalizations((prevcurrentLocalizations) => ({
            ...prevcurrentLocalizations,
            [lang]: {
                ...prevcurrentLocalizations[lang],
                dynamic: {
                    ...prevcurrentLocalizations[lang]?.dynamic,
                    [event.target.name]: event.target.value,
                },
            },
        }));
    };

    /**
     * Change handle for nested proporties
     * @param {event} event the event object
     * @param {String} path the path to the nested property seperated by dots
     */

    const handleNestedChange = (event, path) => {
        setCustomizations((prevCustomizations) => {
            const newState = { ...prevCustomizations };
            const keys = path.split(".");
            let obj = newState;

            // Navigate to the parent of the final key, creating objects as needed
            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                // Create a copy of nested object/array to avoid mutation
                if (Array.isArray(obj[key])) {
                    obj[key] = [...obj[key]];
                } else if (typeof obj[key] === "object" && obj[key] !== null) {
                    obj[key] = { ...obj[key] };
                } else {
                    obj[key] = {};
                }
                obj = obj[key];
            }

            // Set the final value
            const finalKey = keys[keys.length - 1];
            obj[finalKey] =
                event.target.type === "checkbox"
                    ? event.target.checked
                    : event.target.value;

            return newState;
        });
    };

    // const handleNestedChange = (event, path) => {
    //     setCustomizations((prevCustomizations) => {
    //         const newState = { ...prevCustomizations };
    //         const keys = path.split(".");
    //         let obj = newState;
    //         while (keys.length > 1) {
    //             const key = keys.shift();
    //             obj = obj[key] ? obj[key] : {};
    //         }
    //         obj[keys[0]] =
    //             event.target.type === "checkbox"
    //                 ? event.target.checked
    //                 : event.target.value;
    //         return newState;
    //     });
    // };

    /**
     * Add a new group to the POS customization object with the corresponding section
     * the id of the group is set to the maximum id value calculated by getMaxId() function + 1
     * the first field of the group is set to the first field in the fieldNames array
     * and id is set to 1 and relation to "or"
     * @param {String} section the section to add the new group to (tickets or permits)
     */
    const addPOSGroup = (section) => {
        const currentMaxId = getMaxId(customizations?.pos?.[section]);
        const newItem = {
            id: currentMaxId + 1,
            fields: [{ id: 1, fieldName: fieldNames?.[0], rel: "or" }],
        };
        // if (!customizations?.pos?.[section]) {
        //     setCustomizations((prevState) => ({
        //         ...prevState,
        //         pos: {
        //             [section]: [newItem],
        //         },
        //     }));
        // } else {
        setCustomizations((prevState) => ({
            ...prevState,
            pos: {
                ...prevState?.pos,
                [section]: [...prevState?.pos?.[section], newItem],
            },
        }));
        // }
    };

    /**
     * Add a new field to the POS customization object with the corresponding section and group index
     * the id of the field is set to the maximum id value calculated by getMaxId() function + 1
     * the field name is set to the first field in the fieldNames array
     * and relation to "or"
     * @param {String} section the section to add the new field to (tickets or permits)
     * @param {Integer} groupIdx the group index to add the field to
     */
    const addField = (section, groupIdx) => {
        const currentMaxId = getMaxId(
            customizations?.pos?.[section]?.[groupIdx]?.fields,
        );

        const newItem = {
            id: currentMaxId + 1,
            fieldName: fieldNames?.[0],
            rel: "or",
        };
        if (customizations && customizations?.pos)
            setCustomizations((prevState) => ({
                ...prevState,
                pos: {
                    ...prevState?.pos,
                    [section]: prevState?.pos?.[section].map((group, index) =>
                        index === groupIdx
                            ? {
                                  ...group,
                                  fields: [...group.fields, newItem],
                              }
                            : group,
                    ),
                },
            }));
    };
    /**
     * Delete search group by section and group id
     * @param {String} section the section to delete the group from (tickets or permits)
     * @param {Integer} id the id of the group to delete
     */
    const deletePOSGroup = (section, id) => {
        setCustomizations((prevState) => ({
            ...prevState,
            pos: {
                ...prevState?.pos,
                [section]: prevState.pos?.[section]
                    ? prevState.pos[section].filter((item) => item?.id !== id)
                    : [],
            },
        }));
    };

    /**
     *  Delete goup field by section, group index and field index
     * @param {String} section  the section to delete the field from (tickets or permits)
     * @param {Integer} groupIdx  the group index to delete the field from
     * @param {Integer} fieldIdx the field index to delete
     */
    const deleteField = (section, groupIdx, fieldIdx) => {
        setCustomizations((prevState) => ({
            ...prevState,
            pos: {
                ...prevState.pos,
                [section]: prevState.pos[section].map((group, index) =>
                    index === groupIdx
                        ? {
                              ...group,
                              fields: group.fields.filter(
                                  (field, idx) => idx !== fieldIdx,
                              ),
                          }
                        : group,
                ),
            },
        }));
    };

    /**
     *  shaow and hide the card by changing the show card state object (index as a key and boolean as a value)
     * @param {Integer} idx the index of the card to show or hide
     */
    const showCard = (idx) => {
        setCardsVisible((prevState) => ({
            ...prevState,
            [idx]: !prevState?.[idx],
        }));
    };
    /**
     *  show and hide the terms editor by changing the termVisible state object
     * @param {string} language the language of the term editor to show or hide
     */
    const showTermsEditor = (language) => {
        setTermsEditorVisible((prevState) => ({
            ...prevState,
            [language]: !prevState?.[language],
        }));
    };
    /**
     *  show and hide the disclaimer editor by changing the disclaimerVisible state object
     * @param {string} language the language of the disclaimer editor to show or hide
     */
    const showDisclaimerEditor = (language) => {
        setDisclaimerEditorVisible((prevState) => ({
            ...prevState,
            [language]: !prevState?.[language],
        }));
    };

    // The POS customization result for search groups and fields is
    // an array of objects with the group id and the fields array
    // Example:
    // [
    //     {
    //     "id": 2,
    //     "fields": [
    //         {
    //         "id": 1,
    //         "fieldName": "Ticketno",
    //         "rel": "or",
    //         "label": "ticket number"
    //         },
    //         {
    //         "id": 2,
    //         "fieldName": "Plate",
    //         "rel": "or",
    //         "label": "licence plate"
    //         }
    //     ]
    //     },
    //     {
    //     "id": 3,
    //     "fields": [
    //         {
    //         "id": 1,
    //         "fieldName": "Ticketno",
    //         "rel": "and",
    //         "label": "ticket number"
    //         },
    //         {
    //         "id": 2,
    //         "fieldName": "Plate",
    //         "rel": "or",
    //         "label": "licence plate"
    //         }
    //     ]
    //     }
    // ]

    return (
        <article className={`main-page-container flex-container`}>
            <section
                className={` ${styles.main_page_container} main-container flex-container`}
            >
                {!isServiceDataAvailable ? (
                    <div className={` main-page-container flex-container`}>
                        <Spinner />
                    </div>
                ) : !isAdmin ? (
                    // Show the login form
                    <LogIn
                        setIsAdmin={setIsAdmin}
                        setIsSuperUser={setIsSuperUser}
                        isAdmin={isAdmin}
                    />
                ) : resultModal ? (
                    // Show the result modal after submitting the customization form
                    // The result depends on the success state and inProcess state
                    <Modal inProcess={inProcess} success={success} />
                ) : (
                    // Show the customization form if the user is authenticated
                    <form
                        onSubmit={handleSubmit}
                        className={`${styles.form} v-flex-container `}
                    >
                        <h1 className={`${styles.form_header}`}>
                            {t(`customizations`)}
                        </h1>

                        <h3 className={`${styles.category_header}`}>
                            {t(`info`)}:
                        </h3>
                        <fieldset
                            className={`${styles.group_container} flex-container`}
                        >
                            <div
                                className={`${styles.input_container} v-flex-container`}
                            >
                                <div
                                    className={` ${styles.wrap_input_container} v-flex-container`}
                                >
                                    {languages?.length &&
                                        languages.map((language) => (
                                            <div
                                                className={`${styles.wrap_input}`}
                                                key={`logoLine1-${language}`}
                                            >
                                                <InputText
                                                    data={language}
                                                    label={
                                                        `${
                                                            language.split(
                                                                "-",
                                                            )[0]
                                                        } ` +
                                                        `${t("logo text line")} 1`
                                                    }
                                                    inputId={`${
                                                        language.split("-")[0]
                                                    }-logoLine1`}
                                                    name={"logoLine1"}
                                                    inputValue={
                                                        currentLocalizations?.[
                                                            language
                                                        ]?.dynamic?.logoLine1
                                                    }
                                                    handleChangeFunction={
                                                        handleChangeLocal
                                                    }
                                                />
                                            </div>
                                        ))}
                                </div>
                                <div
                                    className={` ${styles.wrap_input_container} v-flex-container`}
                                >
                                    {languages?.length &&
                                        languages.map((language) => (
                                            <div
                                                className={`${styles.wrap_input}`}
                                                key={`logoLine2-${language}`}
                                            >
                                                <InputText
                                                    data={language}
                                                    label={
                                                        `${
                                                            language.split(
                                                                "-",
                                                            )[0]
                                                        } ` +
                                                        `${t("logo text line")} 2`
                                                    }
                                                    inputId={`${
                                                        language.split("-")[0]
                                                    }-logoLine2`}
                                                    name={"logoLine2"}
                                                    inputValue={
                                                        currentLocalizations?.[
                                                            language
                                                        ]?.dynamic?.logoLine2
                                                    }
                                                    handleChangeFunction={
                                                        handleChangeLocal
                                                    }
                                                />
                                            </div>
                                        ))}
                                </div>
                                {!showPOS ? (
                                    <div
                                        className={` ${styles.wrap_input_container} v-flex-container`}
                                    >
                                        {languages?.length &&
                                            languages.map((language) => (
                                                <div
                                                    className={`${styles.wrap_input}`}
                                                    key={`slogan-${language}`}
                                                >
                                                    <InputText
                                                        data={language}
                                                        label={
                                                            `${
                                                                language.split(
                                                                    "-",
                                                                )[0]
                                                            } ` + t("slogan")
                                                        }
                                                        inputId={`${
                                                            language.split(
                                                                "-",
                                                            )[0]
                                                        }-footerHeader`}
                                                        name={"footerHeader"}
                                                        inputValue={
                                                            currentLocalizations?.[
                                                                language
                                                            ]?.dynamic
                                                                ?.footerHeader
                                                        }
                                                        handleChangeFunction={
                                                            handleChangeLocal
                                                        }
                                                    />
                                                </div>
                                            ))}
                                    </div>
                                ) : (
                                    <></>
                                )}

                                {/* <div>
                                    <InputText
                                        label={t(`customer id`)}
                                        inputId="id"
                                        inputValue={customizations?.id}
                                        handleChangeFunction={handleChange}
                                    />
                                </div> */}
                            </div>
                            <div
                                className={`${styles.input_container} v-flex-container`}
                            >
                                <div
                                    className={` ${styles.wrap_input_container} v-flex-container`}
                                >
                                    <InputFile
                                        label={t(`logo`)}
                                        inputId="logo"
                                        inputValue={customizations?.logo}
                                        handleChangeFunction={handleChange}
                                    />
                                </div>
                                {!showPOS ? (
                                    <div
                                        className={` ${styles.wrap_input_container} v-flex-container`}
                                    >
                                        <InputFile
                                            label={t(`print out`)}
                                            inputId="printOut"
                                            inputValue={
                                                customizations?.printOut
                                            }
                                            handleChangeFunction={handleChange}
                                            rastFile={true}
                                        />
                                    </div>
                                ) : (
                                    <></>
                                )}
                                <div
                                    className={`${styles.color_input} flex-container`}
                                >
                                    <InputColor
                                        label={t(`theme color`)}
                                        inputId="current"
                                        inputValue={
                                            customizations?.color?.current
                                        }
                                        handleChangeFunction={(event) =>
                                            handleNestedChange(
                                                event,
                                                "color.current",
                                            )
                                        }
                                        defaultColor={"#009DE0"}
                                    />
                                </div>
                                <div
                                    className={`${styles.color_input} flex-container`}
                                >
                                    <InputColor
                                        label={t(`error color`)}
                                        inputId="error"
                                        inputValue={
                                            customizations?.color?.error ??
                                            "#000000"
                                        }
                                        handleChangeFunction={(event) =>
                                            handleNestedChange(
                                                event,
                                                "color.error",
                                            )
                                        }
                                        defaultColor={"#FF0000"}
                                    />
                                </div>
                                <div
                                    className={` ${styles.wrap_input_container} v-flex-container`}
                                >
                                    <InputText
                                        label={t(`company`)}
                                        inputId="company"
                                        inputValue={customizations?.company}
                                        handleChangeFunction={handleChange}
                                    />
                                </div>
                            </div>
                        </fieldset>
                        {/* Show the POS customization form if the showPOS state is true */}
                        {/* ____________________________________________ PAYMENT _____________________________________________ */}
                        {!showPOS ? (
                            <>
                                {/* __________________________________________________________Nav Buttons */}
                                <h3 className={`${styles.category_header}`}>
                                    {t(`nav buttons`)}:
                                </h3>
                                <fieldset
                                    className={`${styles.group_container} flex-container`}
                                >
                                    {customizations?.nav?.map((item, idx) => (
                                        <div
                                            key={`${idx} ${item?.id}`}
                                            className={`${styles.input_container} v-flex-container`}
                                        >
                                            <h3
                                                className={`${styles.sub_header}`}
                                            >
                                                {t(`button`)} {idx + 1}
                                            </h3>
                                            <div
                                                className={`${styles.checkbox_container}`}
                                            >
                                                <SliderToggle
                                                    inputId={`b${
                                                        idx + 1
                                                    }Enable`}
                                                    inputValue={
                                                        !!customizations?.nav?.[
                                                            idx
                                                        ]?.enable
                                                    }
                                                    onClickFunction={(event) =>
                                                        handleNestedChange(
                                                            event,
                                                            `nav.${idx}.enable`,
                                                        )
                                                    }
                                                    rightLabel={"enable"}
                                                    small={true}
                                                />
                                                {/* <InputCheck
                                                    label={"disable "}
                                                    inputId={`b${
                                                        idx + 1
                                                    }Disable`}
                                                    inputValue={
                                                        !!customizations?.nav?.[
                                                            idx
                                                        ]?.disable
                                                    }
                                                    handleChangeFunction={(
                                                        event
                                                    ) =>
                                                        handleNestedChange(
                                                            event,
                                                            `nav.${idx}.disable`
                                                        )
                                                    }
                                                /> */}
                                            </div>

                                            <div
                                                className={` ${styles.wrap_input_container} v-flex-container`}
                                            >
                                                {languages.map((language) => (
                                                    <div
                                                        className={`${styles.wrap_input}`}
                                                        key={`${language}${item?.id}Body`}
                                                    >
                                                        <InputText
                                                            data={language}
                                                            label={
                                                                `${
                                                                    language.split(
                                                                        "-",
                                                                    )[0]
                                                                } ` +
                                                                t(`button`)
                                                            }
                                                            inputId={`${
                                                                language.split(
                                                                    "-",
                                                                )[0]
                                                            }-${item?.id}Body`}
                                                            name={`${item?.id}Body`}
                                                            inputValue={
                                                                currentLocalizations?.[
                                                                    language
                                                                ]?.dynamic?.[
                                                                    `${item?.id}Body`
                                                                ]
                                                            }
                                                            disable={
                                                                !customizations
                                                                    ?.nav?.[idx]
                                                                    ?.enable
                                                            }
                                                            handleChangeFunction={
                                                                handleChangeLocal
                                                            }
                                                        />
                                                    </div>
                                                ))}
                                            </div>

                                            <div>
                                                <InputText
                                                    label={t(`link`)}
                                                    inputId={`${idx}-link`}
                                                    name={"link"}
                                                    inputValue={
                                                        customizations?.nav?.[
                                                            idx
                                                        ]?.link
                                                    }
                                                    disable={
                                                        !customizations?.nav?.[
                                                            idx
                                                        ]?.enable
                                                    }
                                                    handleChangeFunction={(
                                                        event,
                                                    ) =>
                                                        handleNestedChange(
                                                            event,
                                                            `nav.${idx}.link`,
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </fieldset>
                                {/* ______________________________________________________________________Footer */}

                                <h3 className={`${styles.category_header}`}>
                                    {t(`footer`)}:
                                </h3>

                                {/* ______________________________________________________________________Cards */}
                                <fieldset
                                    className={`${styles.group_container} v-flex-container`}
                                >
                                    {customizations?.footer?.cards?.map(
                                        (card, cardIdx) => (
                                            <div
                                                key={`${cardIdx} ${card?.id}`}
                                                className={`${styles.card_container} v-flex-container`}
                                            >
                                                <div
                                                    className={`${styles.card_header_container} flex-container`}
                                                >
                                                    <div
                                                        className={`${styles.footer_checkbox_container} flex-container`}
                                                    >
                                                        <SliderToggle
                                                            inputId={`${card?.id}Enable`}
                                                            inputValue={
                                                                !!customizations
                                                                    ?.footer
                                                                    ?.cards?.[
                                                                    cardIdx
                                                                ]?.enable
                                                            }
                                                            onClickFunction={(
                                                                event,
                                                            ) =>
                                                                handleNestedChange(
                                                                    event,
                                                                    `footer.cards.${cardIdx}.enable`,
                                                                )
                                                            }
                                                            rightLabel={
                                                                "enable"
                                                            }
                                                        />
                                                    </div>
                                                    <h3
                                                        className={`${styles.card_header} flex-container`}
                                                    >
                                                        {t(`card`)}{" "}
                                                        {cardIdx + 1}
                                                    </h3>
                                                </div>

                                                <div
                                                    className={`${styles.collapse_button_container}`}
                                                >
                                                    <ButtonExpand
                                                        CurrentState={
                                                            cardsVisible?.[
                                                                cardIdx
                                                            ]
                                                        }
                                                        onClickFunction={() =>
                                                            showCard(cardIdx)
                                                        }
                                                    />
                                                </div>
                                                {
                                                    <div
                                                        className={`${
                                                            styles.card_expandable_container
                                                        } ${
                                                            cardsVisible?.[
                                                                cardIdx
                                                            ] === true
                                                                ? styles.expand
                                                                : ""
                                                        } `}
                                                    >
                                                        <div
                                                            className={`${styles.card_group_container} flex-container`}
                                                        >
                                                            <div
                                                                className={`${styles.input_container} ${styles.card_input}  flex-container`}
                                                            >
                                                                <div
                                                                    className={` ${styles.wrap_input_container} v-flex-container`}
                                                                >
                                                                    {languages.map(
                                                                        (
                                                                            language,
                                                                        ) => (
                                                                            <div
                                                                                className={`${styles.wrap_input}`}
                                                                                key={`${language}-${card?.id}Header`}
                                                                            >
                                                                                <InputText
                                                                                    data={
                                                                                        language
                                                                                    }
                                                                                    label={
                                                                                        `${
                                                                                            language.split(
                                                                                                "-",
                                                                                            )[0]
                                                                                        } ` +
                                                                                        t(
                                                                                            `header`,
                                                                                        )
                                                                                    }
                                                                                    inputId={`${
                                                                                        language.split(
                                                                                            "-",
                                                                                        )[0]
                                                                                    }-${
                                                                                        card?.id
                                                                                    }Header`}
                                                                                    name={`${card?.id}Header`}
                                                                                    inputValue={
                                                                                        currentLocalizations?.[
                                                                                            language
                                                                                        ]
                                                                                            ?.dynamic?.[
                                                                                            `${card?.id}Header`
                                                                                        ]
                                                                                    }
                                                                                    disable={
                                                                                        !customizations
                                                                                            ?.footer
                                                                                            ?.cards?.[
                                                                                            cardIdx
                                                                                        ]
                                                                                            ?.enable
                                                                                    }
                                                                                    handleChangeFunction={
                                                                                        handleChangeLocal
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div
                                                                className={`${styles.input_container} ${styles.card_input}  flex-container`}
                                                            >
                                                                <div
                                                                    className={` ${styles.wrap_input_container} v-flex-container`}
                                                                >
                                                                    {languages.map(
                                                                        (
                                                                            language,
                                                                        ) => (
                                                                            <div
                                                                                className={`${styles.wrap_input}`}
                                                                                key={`${language}-${card?.id}Message`}
                                                                            >
                                                                                <InputText
                                                                                    data={
                                                                                        language
                                                                                    }
                                                                                    label={
                                                                                        `${
                                                                                            language.split(
                                                                                                "-",
                                                                                            )[0]
                                                                                        } ` +
                                                                                        t(
                                                                                            `message`,
                                                                                        )
                                                                                    }
                                                                                    inputId={`${
                                                                                        language.split(
                                                                                            "-",
                                                                                        )[0]
                                                                                    }-${
                                                                                        card?.id
                                                                                    }Message`}
                                                                                    name={`${card?.id}Message`}
                                                                                    inputValue={
                                                                                        currentLocalizations?.[
                                                                                            language
                                                                                        ]
                                                                                            ?.dynamic?.[
                                                                                            `${card?.id}Message`
                                                                                        ]
                                                                                    }
                                                                                    disable={
                                                                                        !customizations
                                                                                            ?.footer
                                                                                            ?.cards?.[
                                                                                            cardIdx
                                                                                        ]
                                                                                            ?.enable
                                                                                    }
                                                                                    handleChangeFunction={
                                                                                        handleChangeLocal
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {/* ____________________________________________________________________Links */}
                                                        <div
                                                            className={`${styles.card_group_container} flex-container`}
                                                        >
                                                            {customizations?.footer?.cards?.[
                                                                cardIdx
                                                            ]?.links.map(
                                                                (
                                                                    link,
                                                                    linkIdx,
                                                                ) => (
                                                                    <div
                                                                        key={`${card?.id}${link?.id}${linkIdx}`}
                                                                        className={`${styles.input_container}  ${styles.links_container} v-flex-container`}
                                                                    >
                                                                        <h3
                                                                            className={`${styles.sub_header}`}
                                                                        >
                                                                            {t(
                                                                                `link`,
                                                                            )}{" "}
                                                                            {linkIdx +
                                                                                1}
                                                                        </h3>

                                                                        <div
                                                                            className={`${styles.checkbox_container}`}
                                                                        >
                                                                            <SliderToggle
                                                                                inputId={`${card?.id}-${link?.id}-Enable`}
                                                                                small={
                                                                                    true
                                                                                }
                                                                                disable={
                                                                                    !customizations
                                                                                        ?.footer
                                                                                        ?.cards?.[
                                                                                        cardIdx
                                                                                    ]
                                                                                        ?.enable
                                                                                }
                                                                                inputValue={
                                                                                    !!customizations
                                                                                        ?.footer
                                                                                        ?.cards?.[
                                                                                        cardIdx
                                                                                    ]
                                                                                        ?.links?.[
                                                                                        linkIdx
                                                                                    ]
                                                                                        ?.enable
                                                                                }
                                                                                onClickFunction={(
                                                                                    event,
                                                                                ) =>
                                                                                    handleNestedChange(
                                                                                        event,
                                                                                        `footer.cards.${cardIdx}.links.${linkIdx}.enable`,
                                                                                    )
                                                                                }
                                                                                rightLabel={
                                                                                    "enable"
                                                                                }
                                                                            />
                                                                        </div>

                                                                        <div>
                                                                            <InputList
                                                                                label={t(
                                                                                    `type`,
                                                                                )}
                                                                                inputId={`${card?.id}${link?.id}${linkIdx}-type`}
                                                                                inputValue={
                                                                                    customizations
                                                                                        ?.footer
                                                                                        ?.cards?.[
                                                                                        cardIdx
                                                                                    ]
                                                                                        ?.links?.[
                                                                                        linkIdx
                                                                                    ]
                                                                                        ?.type
                                                                                }
                                                                                handleChangeFunction={(
                                                                                    event,
                                                                                ) =>
                                                                                    handleNestedChange(
                                                                                        event,
                                                                                        `footer.cards.${cardIdx}.links.${linkIdx}.type`,
                                                                                    )
                                                                                }
                                                                                disable={
                                                                                    !customizations
                                                                                        ?.footer
                                                                                        ?.cards?.[
                                                                                        cardIdx
                                                                                    ]
                                                                                        ?.enable ||
                                                                                    !customizations
                                                                                        ?.footer
                                                                                        ?.cards?.[
                                                                                        cardIdx
                                                                                    ]
                                                                                        ?.links?.[
                                                                                        linkIdx
                                                                                    ]
                                                                                        ?.enable
                                                                                }
                                                                                values={
                                                                                    LINK_TYPES
                                                                                }
                                                                            />
                                                                        </div>
                                                                        {customizations
                                                                            ?.footer
                                                                            ?.cards?.[
                                                                            cardIdx
                                                                        ]
                                                                            ?.links?.[
                                                                            linkIdx
                                                                        ]
                                                                            ?.type !==
                                                                        "blank" ? (
                                                                            customizations
                                                                                ?.footer
                                                                                ?.cards?.[
                                                                                cardIdx
                                                                            ]
                                                                                ?.links?.[
                                                                                linkIdx
                                                                            ]
                                                                                ?.type ===
                                                                            "social-media" ? (
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
                                                                                //  !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

                                                                                <>
                                                                                    <div>
                                                                                        <InputText
                                                                                            label={t(
                                                                                                `facebook`,
                                                                                            )}
                                                                                            inputId={`${card?.id}${link?.id}${linkIdx}-facebook`}
                                                                                            name="facebook"
                                                                                            inputValue={
                                                                                                customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.social
                                                                                                    ?.facebook
                                                                                            }
                                                                                            disable={
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.enable ||
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.enable
                                                                                            }
                                                                                            handleChangeFunction={(
                                                                                                event,
                                                                                            ) =>
                                                                                                handleNestedChange(
                                                                                                    event,
                                                                                                    `footer.cards.${cardIdx}.links.${linkIdx}.social.facebook`,
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                    <div>
                                                                                        <InputText
                                                                                            label={t(
                                                                                                `x`,
                                                                                            )}
                                                                                            inputId={`${card?.id}${link?.id}${linkIdx}-x`}
                                                                                            name="x"
                                                                                            inputValue={
                                                                                                customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.social
                                                                                                    ?.x
                                                                                            }
                                                                                            disable={
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.enable ||
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.enable
                                                                                            }
                                                                                            handleChangeFunction={(
                                                                                                event,
                                                                                            ) =>
                                                                                                handleNestedChange(
                                                                                                    event,
                                                                                                    `footer.cards.${cardIdx}.links.${linkIdx}.social.x`,
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                    <div>
                                                                                        <InputText
                                                                                            label={t(
                                                                                                `youtube`,
                                                                                            )}
                                                                                            inputId={`${card?.id}${link?.id}${linkIdx}-youtube`}
                                                                                            name="youtube"
                                                                                            inputValue={
                                                                                                customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.social
                                                                                                    ?.youtube
                                                                                            }
                                                                                            disable={
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.enable ||
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.enable
                                                                                            }
                                                                                            handleChangeFunction={(
                                                                                                event,
                                                                                            ) =>
                                                                                                handleNestedChange(
                                                                                                    event,
                                                                                                    `footer.cards.${cardIdx}.links.${linkIdx}.social.youtube`,
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                    <div>
                                                                                        <InputText
                                                                                            label={t(
                                                                                                `linkedin`,
                                                                                            )}
                                                                                            inputId={`${card?.id}${link?.id}${linkIdx}-linkedin`}
                                                                                            name="linkedin"
                                                                                            inputValue={
                                                                                                customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.social
                                                                                                    ?.linkedin
                                                                                            }
                                                                                            disable={
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.enable ||
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.enable
                                                                                            }
                                                                                            handleChangeFunction={(
                                                                                                event,
                                                                                            ) =>
                                                                                                handleNestedChange(
                                                                                                    event,
                                                                                                    `footer.cards.${cardIdx}.links.${linkIdx}.social.linkedin`,
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <div>
                                                                                        <InputList
                                                                                            label={t(
                                                                                                `icon`,
                                                                                            )}
                                                                                            inputId={`${card?.id}${link?.id}${linkIdx}-icon`}
                                                                                            inputValue={
                                                                                                customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.icon
                                                                                            }
                                                                                            handleChangeFunction={(
                                                                                                event,
                                                                                            ) =>
                                                                                                handleNestedChange(
                                                                                                    event,
                                                                                                    `footer.cards.${cardIdx}.links.${linkIdx}.icon`,
                                                                                                )
                                                                                            }
                                                                                            disable={
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.enable ||
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.enable
                                                                                            }
                                                                                            values={
                                                                                                LINK_ICONS
                                                                                            }
                                                                                        />
                                                                                    </div>

                                                                                    <div
                                                                                        className={` ${styles.wrap_input_container} v-flex-container`}
                                                                                    >
                                                                                        {languages.map(
                                                                                            (
                                                                                                language,
                                                                                            ) => (
                                                                                                <div
                                                                                                    className={`${styles.wrap_input}`}
                                                                                                    key={`${language}${card?.id}${link?.id}${linkIdx}Line1`}
                                                                                                >
                                                                                                    <InputText
                                                                                                        data={
                                                                                                            language
                                                                                                        }
                                                                                                        label={
                                                                                                            `${
                                                                                                                language.split(
                                                                                                                    "-",
                                                                                                                )[0]
                                                                                                            } ` +
                                                                                                            t(
                                                                                                                `line`,
                                                                                                            ) +
                                                                                                            ` 1`
                                                                                                        }
                                                                                                        inputId={`${
                                                                                                            language.split(
                                                                                                                "-",
                                                                                                            )[0]
                                                                                                        }-${
                                                                                                            card?.id
                                                                                                        }${
                                                                                                            link?.id
                                                                                                        }Line1`}
                                                                                                        name={`${card?.id}${link?.id}Line1`}
                                                                                                        inputValue={
                                                                                                            currentLocalizations?.[
                                                                                                                language
                                                                                                            ]
                                                                                                                ?.dynamic?.[
                                                                                                                `${card?.id}${link?.id}Line1`
                                                                                                            ]
                                                                                                        }
                                                                                                        disable={
                                                                                                            !customizations
                                                                                                                ?.footer
                                                                                                                ?.cards?.[
                                                                                                                cardIdx
                                                                                                            ]
                                                                                                                ?.enable ||
                                                                                                            !customizations
                                                                                                                ?.footer
                                                                                                                ?.cards?.[
                                                                                                                cardIdx
                                                                                                            ]
                                                                                                                ?.links?.[
                                                                                                                linkIdx
                                                                                                            ]
                                                                                                                ?.enable
                                                                                                        }
                                                                                                        handleChangeFunction={
                                                                                                            handleChangeLocal
                                                                                                        }
                                                                                                    />
                                                                                                </div>
                                                                                            ),
                                                                                        )}
                                                                                    </div>
                                                                                    <div
                                                                                        className={` ${styles.wrap_input_container} v-flex-container`}
                                                                                    >
                                                                                        {languages.map(
                                                                                            (
                                                                                                language,
                                                                                            ) => (
                                                                                                <div
                                                                                                    className={`${styles.wrap_input}`}
                                                                                                    key={`${language}${card?.id}${link?.id}${linkIdx}Line2`}
                                                                                                >
                                                                                                    <InputText
                                                                                                        data={
                                                                                                            language
                                                                                                        }
                                                                                                        label={
                                                                                                            `${
                                                                                                                language.split(
                                                                                                                    "-",
                                                                                                                )[0]
                                                                                                            } ` +
                                                                                                            t(
                                                                                                                `line`,
                                                                                                            ) +
                                                                                                            ` 2`
                                                                                                        }
                                                                                                        inputId={`${
                                                                                                            language.split(
                                                                                                                "-",
                                                                                                            )[0]
                                                                                                        }-${
                                                                                                            card?.id
                                                                                                        }${
                                                                                                            link?.id
                                                                                                        }Line2`}
                                                                                                        name={`${card?.id}${link?.id}Line2`}
                                                                                                        inputValue={
                                                                                                            currentLocalizations?.[
                                                                                                                language
                                                                                                            ]
                                                                                                                ?.dynamic?.[
                                                                                                                `${card?.id}${link?.id}Line2`
                                                                                                            ]
                                                                                                        }
                                                                                                        disable={
                                                                                                            !customizations
                                                                                                                ?.footer
                                                                                                                ?.cards?.[
                                                                                                                cardIdx
                                                                                                            ]
                                                                                                                ?.enable ||
                                                                                                            !customizations
                                                                                                                ?.footer
                                                                                                                ?.cards?.[
                                                                                                                cardIdx
                                                                                                            ]
                                                                                                                ?.links?.[
                                                                                                                linkIdx
                                                                                                            ]
                                                                                                                ?.enable
                                                                                                        }
                                                                                                        handleChangeFunction={
                                                                                                            handleChangeLocal
                                                                                                        }
                                                                                                    />
                                                                                                </div>
                                                                                            ),
                                                                                        )}
                                                                                    </div>

                                                                                    <div>
                                                                                        <InputText
                                                                                            label={t(
                                                                                                `details`,
                                                                                            )}
                                                                                            inputId={`${card?.id}${link?.id}${linkIdx}-details`}
                                                                                            name="details"
                                                                                            inputValue={
                                                                                                customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.details
                                                                                            }
                                                                                            disable={
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.enable ||
                                                                                                !customizations
                                                                                                    ?.footer
                                                                                                    ?.cards?.[
                                                                                                    cardIdx
                                                                                                ]
                                                                                                    ?.links?.[
                                                                                                    linkIdx
                                                                                                ]
                                                                                                    ?.enable
                                                                                            }
                                                                                            handleChangeFunction={(
                                                                                                event,
                                                                                            ) =>
                                                                                                handleNestedChange(
                                                                                                    event,
                                                                                                    `footer.cards.${cardIdx}.links.${linkIdx}.details`,
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                </>
                                                                            )
                                                                        ) : (
                                                                            <>

                                                                            </>
                                                                        )}
                                                                    </div>
                                                                ),
                                                            )}
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        ),
                                    )}
                                </fieldset>
                                {/* ____________________________________________________________________Terms */}

                                <h3 className={`${styles.category_header}`}>
                                    {t(`terms and conditions`)}:
                                </h3>

                                <fieldset
                                    className={`${styles.group_container} v-flex-container`}
                                >
                                    {languages.map((language) => (
                                        <div
                                            key={`terms_editor_${language}`}
                                            className={`${styles.main_editor_container} ${styles.group_container}`}
                                        >
                                            <h3
                                                className={`${styles.card_header}`}
                                            >
                                                {language}
                                            </h3>

                                            {/* <InputFile
                                                label={"import file"}
                                                inputId={`${language}-import-file`}
                                                inputValue={
                                                    termsFileImport?.[language]
                                                }
                                                handleChangeFunction={(
                                                    currentFile
                                                ) => {
                                                    setTermsFileImport(
                                                        (prev) => ({
                                                            ...prev,
                                                            [language]:
                                                                currentFile
                                                                    .target
                                                                    .value,
                                                        })
                                                    );
                                                }}
                                            /> */}

                                            <div
                                                className={`${styles.collapse_button_container}`}
                                            >
                                                <ButtonExpand
                                                    CurrentState={
                                                        termsEditorVisible?.[
                                                            language
                                                        ]
                                                    }
                                                    onClickFunction={() =>
                                                        showTermsEditor(
                                                            language,
                                                        )
                                                    }
                                                />
                                            </div>

                                            {
                                                <div
                                                    className={`${
                                                        styles.card_expandable_container
                                                    } ${
                                                        termsEditorVisible?.[
                                                            language
                                                        ] === true
                                                            ? styles.expand
                                                            : ""
                                                    } `}
                                                >
                                                    <RTEditor
                                                        value={
                                                            termsJson?.[
                                                                language
                                                            ]
                                                        }
                                                        onChange={(
                                                            currentJson,
                                                        ) => {
                                                            setTermsJson(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    [language]:
                                                                        currentJson,
                                                                }),
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            }
                                        </div>
                                    ))}
                                </fieldset>

                                {/* ____________________________________________________________________Disclaimer */}
                                {isSuperUser ? (
                                    <>
                                        <h3
                                            className={`${styles.category_header}`}
                                        >
                                            {t(`disclaimer`)}:
                                        </h3>
                                        <fieldset
                                            className={`${styles.group_container} v-flex-container`}
                                        >
                                            {languages.map((language) => (
                                                <div
                                                    key={`disclaimer_editor_${language}`}
                                                    className={`${styles.main_editor_container} ${styles.group_container}`}
                                                >
                                                    <h3
                                                        className={`${styles.card_header}`}
                                                    >
                                                        {language}
                                                    </h3>

                                                    <div
                                                        className={`${styles.collapse_button_container}`}
                                                    >
                                                        <ButtonExpand
                                                            CurrentState={
                                                                disclaimerEditorVisible?.[
                                                                    language
                                                                ]
                                                            }
                                                            onClickFunction={() =>
                                                                showDisclaimerEditor(
                                                                    language,
                                                                )
                                                            }
                                                        />
                                                    </div>

                                                    {
                                                        <div
                                                            className={`${
                                                                styles.card_expandable_container
                                                            } ${
                                                                disclaimerEditorVisible?.[
                                                                    language
                                                                ] === true
                                                                    ? styles.expand
                                                                    : ""
                                                            } `}
                                                        >
                                                            <RTEditor
                                                                value={
                                                                    disclaimerJson?.[
                                                                        language
                                                                    ]
                                                                }
                                                                onChange={(
                                                                    currentJson,
                                                                ) => {
                                                                    setDisclaimerJson(
                                                                        (
                                                                            prev,
                                                                        ) => ({
                                                                            ...prev,
                                                                            [language]:
                                                                                currentJson,
                                                                        }),
                                                                    );
                                                                }}
                                                            />
                                                        </div>
                                                    }
                                                </div>
                                            ))}
                                        </fieldset>
                                    </>
                                ) : (
                                    <></>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Show the POS customization form if the showPOS state is true */}
                                {/* ____________________________________________ POS _____________________________________________ */}

                                <h3 className={`${styles.category_header}`}>
                                    {t(`ticket's groups`)}:
                                </h3>
                                <fieldset
                                    className={`${styles.group_container} flex-container`}
                                >
                                    <div
                                        className={`${styles.card_group_container} flex-container`}
                                    >
                                        <div
                                            className={`${styles.add_group_button} flex-container`}
                                        >
                                            <Button
                                                body={t("add group")}
                                                size={"small"}
                                                type={"button"}
                                                onClickFuction={() =>
                                                    addPOSGroup("tickets")
                                                }
                                            />
                                        </div>

                                        {customizations?.pos?.tickets &&
                                            customizations?.pos?.tickets.map(
                                                (item, idx) => (
                                                    <CustomSearchGroup
                                                        key={`TG-${idx}`}
                                                        groupType={"tickets"}
                                                        item={item}
                                                        idx={idx}
                                                        customizations={
                                                            customizations
                                                        }
                                                        deletePOSGroup={
                                                            deletePOSGroup
                                                        }
                                                        handleNestedChange={
                                                            handleNestedChange
                                                        }
                                                        deleteField={
                                                            deleteField
                                                        }
                                                        addField={addField}
                                                        categories={fieldNames}
                                                    />
                                                ),
                                            )}
                                    </div>
                                </fieldset>
                                <h3 className={`${styles.category_header}`}>
                                    {t(`permit's groups`)}:
                                </h3>
                                <fieldset
                                    className={`${styles.group_container} flex-container`}
                                >
                                    <div
                                        className={`${styles.card_group_container} flex-container`}
                                    >
                                        <div
                                            className={`${styles.add_group_button} flex-container`}
                                        >
                                            <Button
                                                body={t("add group")}
                                                size={"small"}
                                                type={"button"}
                                                onClickFuction={() =>
                                                    addPOSGroup("permits")
                                                }
                                            />
                                        </div>
                                        {customizations?.pos?.permits &&
                                            customizations?.pos?.permits.map(
                                                (item, idx) => (
                                                    <CustomSearchGroup
                                                        key={`PG-${idx}`}
                                                        groupType={"permits"}
                                                        item={item}
                                                        idx={idx}
                                                        customizations={
                                                            customizations
                                                        }
                                                        deletePOSGroup={
                                                            deletePOSGroup
                                                        }
                                                        handleNestedChange={
                                                            handleNestedChange
                                                        }
                                                        deleteField={
                                                            deleteField
                                                        }
                                                        addField={addField}
                                                        categories={fieldNames}
                                                    />
                                                ),
                                            )}
                                    </div>
                                </fieldset>
                            </>
                        )}

                        <Button body={t(`submit`)} type={"submit"} />
                    </form>
                )}
            </section>
        </article>
    );
};

export default Customization;
